import { createTheme } from "@mui/material/styles";
import { grey, orange } from "./designColors";
import { fontFaces } from "./fontFaces";
import { palette } from "./palette";
import { spacing } from "./spacing";
import { typographyComponents } from "./typographyComponents";
import "./customOptionsVariants";

/**
 * Complete NN brand theme with all MUI component overrides.
 * Import this in any app to get the full branded look.
 */
export const brandTheme = createTheme({
    spacing,
    components: {
        MuiCssBaseline: {
            styleOverrides: `
                ${fontFaces}
                .Mui-disabled + label { color: ${grey["400"]}; }
                ul:not([style]):not([class]) {
                    list-style-type: none;
                    padding: 0 0 0 22px;
                }
                ul:not([style]):not([class]) li {
                    margin-top: 8px;
                }
                ul:not([style]):not([class]) li::before {
                    content: '•';
                    color: ${orange["300"]};
                    display: inline-block;
                    margin-left: -22px;
                    width: 22px;
                    font-size: 22px;
                    line-height: 0;
                    position: relative;
                    top: 3px;
                }
                ul:not([style]):not([class]) ul li::before {
                    color: ${grey["600"]};
                }
                a:not([style]):not([class]) {
                    color: ${orange["500"]};
                }
                ul[data-clear] { margin: 0; }
            `,
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    alignItems: "center",
                },
                message: { flexGrow: 1 },
                icon: { alignItems: "center" },
            },
            variants: [
                {
                    props: { variant: "transparent" },
                    style: {
                        backgroundColor: "transparent",
                        border: "none",
                        padding: 0,
                    },
                },
            ],
        },
        MuiButton: {
            defaultProps: { disableRipple: true, size: "large" },
            styleOverrides: {
                sizeSmall: { fontSize: "1.6rem" },
                sizeMedium: { fontSize: "1.8rem" },
                sizeLarge: { fontSize: "2.0rem" },
                root: {
                    fontFamily: "NNNittiGroteskTextBold",
                    fontWeight: 600,
                    minWidth: 0,
                    textTransform: "none",
                    "&:focus": {
                        outlineColor: "#000000",
                        outlineStyle: "solid",
                        outlineWidth: "3px",
                    },
                },
            },
        },
        MuiButtonBase: {
            styleOverrides: {
                root: {
                    "&.MuiTab-root": {
                        fontFamily: "NNNittiGroteskTextRegular",
                    },
                },
            },
        },
        MuiIconButton: {
            variants: [
                {
                    props: { color: "primary" as const },
                    style: {
                        backgroundColor: orange["500"],
                        color: "#FFFFFF",
                        "&:hover": { backgroundColor: orange["600"] },
                    },
                },
            ],
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    fontFamily: "NNNittiGroteskTextBold",
                    fontWeight: 600,
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: { marginLeft: 0 },
                label: {
                    fontFamily: "NNNittiGroteskTextRegular",
                    fontSize: "1.6rem",
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    border: `1px solid ${grey["400"]}`,
                    borderRadius: 4,
                    width: "100%",
                    position: "relative" as const,
                    "&::before": {
                        content: `""`,
                        display: "block",
                        height: 12,
                        width: 16,
                        position: "absolute" as const,
                        background: "#FFFFFF",
                        right: -1,
                        top: -1,
                    },
                    "&.Mui-focused": {
                        borderColor: grey["800"],
                    },
                    "&.Mui-disabled": {
                        borderColor: grey["400"],
                    },
                    "&.Mui-disabled input": {
                        color: grey["400"],
                    },
                },
                notchedOutline: {
                    border: 0,
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                input: {
                    padding: "16px",
                    "&:focus": {
                        backgroundColor: "transparent",
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontFamily: "NNNittiGroteskTextBold",
                    fontWeight: 600,
                    transform: "none",
                    color: grey["800"],
                    "&.Mui-focused": {
                        color: grey["600"],
                    },
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: { zIndex: 1 },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: { verticalAlign: "middle" },
            },
        },
        MuiNativeSelect: {
            styleOverrides: {
                icon: { color: orange["500"], right: 12 },
            },
        },
        MuiSelect: {
            styleOverrides: {
                icon: { color: orange["500"], right: 12 },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    paddingLeft: 0,
                    "&.Mui-disabled": {
                        color: grey["400"],
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    "&.Mui-checked span": {
                        boxShadow: `0 0 0 1px ${orange["500"]}`,
                    },
                    "&.Mui-disabled span": {
                        boxShadow: `0 0 0 1px ${grey["400"]}`,
                    },
                    "&.Mui-disabled span::before": {
                        backgroundImage: `radial-gradient(${grey["400"]},${grey["400"]} 34%,transparent 38%)`,
                    },
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    width: 56,
                    height: 32,
                    padding: 0,
                    marginRight: 9,
                },
                switchBase: {
                    padding: 0,
                    margin: 1,
                    transitionDuration: "300ms",
                    "&.Mui-checked": {
                        transform: "translateX(24px)",
                        color: "#fff",
                        "& + .MuiSwitch-track": {
                            opacity: 1,
                            border: 0,
                        },
                    },
                    "&.Mui-disabled + .MuiSwitch-track": {
                        backgroundColor: grey["400"],
                        opacity: 1,
                    },
                    "&.Mui-disabled .MuiSwitch-thumb": {
                        color: "#FFFFFF",
                    },
                },
                thumb: {
                    boxSizing: "border-box" as const,
                    width: 30,
                    height: 30,
                },
                track: {
                    borderRadius: 16,
                    backgroundColor: grey["000"],
                    opacity: 1,
                    transition: "background-color 500ms",
                },
            },
        },
        MuiSlider: {
            styleOverrides: {
                thumb: {
                    height: 32,
                    width: 32,
                    backgroundColor: "#FFFFFF",
                    border: "1px solid currentColor",
                    "&.Mui-active": { boxShadow: "none" },
                    "&:hover": { boxShadow: "none" },
                    "&.Mui-focusVisible": { boxShadow: "none" },
                },
                rail: {
                    height: 8,
                    width: "calc(100% + 32px)",
                    marginLeft: -16,
                },
                track: {
                    width: "calc(100% + 32px)",
                    marginLeft: -16,
                },
            },
        },
        MuiPaper: {
            defaultProps: { elevation: 4 },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    paddingTop: 12,
                    paddingBottom: 12,
                },
            },
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    "@media (min-width: 962px)": {
                        paddingLeft: "16px",
                        paddingRight: "16px",
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    height: "auto",
                    width: "auto",
                    maxWidth: "90%",
                    maxHeight: "90%",
                    overflowY: "clip" as const,
                    borderRadius: 16,
                },
                paperFullScreen: {
                    width: "100%",
                    maxWidth: "100%",
                    alignSelf: "flex-end",
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                asterisk: { color: orange["500"] },
            },
        },
        MuiChip: {
            defaultProps: { color: "primary" },
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    height: 16,
                },
                label: {
                    paddingLeft: 4,
                    paddingRight: 4,
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                iconContainer: {
                    paddingRight: 0,
                    width: 12,
                    height: 12,
                    borderRadius: "100%",
                    border: `2px solid ${grey["100"]}`,
                    "&.Mui-active, &.Mui-completed": {
                        backgroundColor: orange["500"],
                        border: `2px solid ${orange["500"]}`,
                    },
                },
            },
        },
        MuiStepConnector: {
            styleOverrides: {
                line: {
                    borderColor: orange["500"],
                    borderTopWidth: 2,
                },
                root: {
                    "&.Mui-disabled .MuiStepConnector-line": {
                        borderColor: grey["100"],
                    },
                },
            },
        },
        MuiStep: {
            styleOverrides: {
                root: { padding: 0 },
            },
        },
        MuiStepper: {
            styleOverrides: {
                root: {
                    "&::before": {
                        content: `""`,
                        display: "flex",
                        flex: "1 1 auto",
                        height: 2,
                        backgroundColor: orange["500"],
                    },
                    "&::after": {
                        content: `""`,
                        display: "flex",
                        flex: "1 1 auto",
                        height: 2,
                        backgroundColor: grey["100"],
                    },
                    "&.last-step::after": {
                        backgroundColor: orange["500"],
                    },
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: 0,
                    marginRight: "12px",
                },
            },
        },
        MuiDrawer: {
            defaultProps: { anchor: "bottom" },
            styleOverrides: {
                paperAnchorBottom: {
                    borderTopRightRadius: 16,
                    borderTopLeftRadius: 16,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    "& > .MuiTab-iconWrapper": {
                        justifyContent: "center",
                        height: "24px",
                    },
                },
            },
        },
    },
    palette,
    customShadows: {
        close: "0px 0px 4px rgba(65, 65, 65, 0.03), 0px 1px 2px rgba(102, 102, 102, 0.08), 0px 0px 6px rgba(128, 128, 128, 0.15), 0px 4px 8px rgba(114, 114, 114, 0.1);",
        far: "0px 4px 12px rgba(65, 65, 65, 0.15)",
        extraFar: "0px -4px 12px rgba(65, 65, 65, 0.15);",
    },
    typography: {
        fontFamily: `NNNittiGroteskTextRegular, sans-serif`,
        body1: typographyComponents.bodyTextM,
        body2: typographyComponents.bodyTextM,
        caption: typographyComponents.bodyTextM,
        overline: typographyComponents.bodyTextM,
        subtitle1: typographyComponents.bodyTextM,
        subtitle2: typographyComponents.bodyTextM,
        ...typographyComponents,
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 962,
            lg: 962,
            xl: 962,
        },
    },
});

// Map custom shadow names to shadow array indices
brandTheme.shadows[4] = brandTheme.customShadows.close;
brandTheme.shadows[16] = brandTheme.customShadows.far;
brandTheme.shadows[24] = brandTheme.customShadows.extraFar;

// Map heading typography
brandTheme.typography.h1 = { ...typographyComponents.headingL };
brandTheme.typography.h2 = { ...typographyComponents.headingM };
brandTheme.typography.h3 = { ...typographyComponents.headingS };
brandTheme.typography.h4 = { ...typographyComponents.headingXS };
